import * as React from 'react';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface DatePickerProps {
  value: Date | null | undefined;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function DatePicker({ value, onChange, placeholder = 'Pick a date', className, disabled }: DatePickerProps) {
  const [open, setOpen] = React.useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            'w-full justify-start text-left font-normal h-10',
            !value && 'text-grey-400',
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? format(value, 'PPP') : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <DayPicker
          mode="single"
          selected={value ?? undefined}
          // Open on the selected day's month, not today's
          defaultMonth={value ?? undefined}
          onSelect={(date) => {
            onChange(date ?? null);
            setOpen(false);
          }}
          classNames={{
            months: 'flex flex-col sm:flex-row gap-2',
            month_caption: 'flex justify-center pt-1 relative items-center text-sm font-medium',
            nav: 'flex items-center gap-1',
            button_previous: 'absolute left-1 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 inline-flex items-center justify-center',
            button_next: 'absolute right-1 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 inline-flex items-center justify-center',
            month_grid: 'w-full border-collapse',
            weekdays: 'flex',
            weekday: 'text-grey-500 rounded-md w-9 font-normal text-[0.8rem]',
            week: 'flex w-full mt-2',
            day: 'h-9 w-9 text-center text-sm p-0 relative [&:has(button)]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
            day_button: 'h-9 w-9 p-0 font-normal inline-flex items-center justify-center rounded-md hover:bg-grey-100 aria-selected:opacity-100',
            selected: 'bg-primary text-white hover:bg-primary hover:text-white focus:bg-primary focus:text-white rounded-md',
            today: 'bg-grey-100 rounded-md',
            outside: 'text-grey-300 aria-selected:text-grey-300',
            disabled: 'text-grey-300',
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
